// ── SmartTraQ Trade Documents Page v2 ──
import React, { useState, useMemo } from "react";
import { T, isDark, Ic, sIG, sSG, sBG, sB2G } from './theme.jsx';
import { HF } from './constants';
import { EmptyPg, DotsMenu, exportCSV } from './ui.jsx';
import { useI18n } from './i18n.js';

function TradeDocsPg({tradeDocs,orders,custs,onView,onEdit,onDelete,onPrint,onBulkDel,nt,companySett}) {
  const {t}=useI18n();
  const [filter,setFilter]=useState("all");
  const [custFilter,setCustFilter]=useState("all");
  const [ordFilter,setOrdFilter]=useState("all");
  const [search,setSearch]=useState("");
  const [sortKey,setSortKey]=useState("date");
  const [sortDir,setSortDir]=useState("desc");
  const [sel,sSel]=useState(new Set());
  const togSel=id=>sSel(p=>{const n=new Set(p);n.has(id)?n.delete(id):n.add(id);return n});

  const types={ci:{n:"Commercial Invoice",s:"CI",c:"#1E40AF",ic:"receipt"},pl:{n:"Packing List",s:"PL",c:"#0E7490",ic:"pkg"}};
  const docs=(tradeDocs||[]);

  // ── Unique customers & orders for filter dropdowns ──
  const custList=useMemo(()=>[...new Set(docs.map(d=>d.customer).filter(Boolean))].sort(),[docs]);
  const ordList=useMemo(()=>{
    const ids=[...new Set(docs.map(d=>d.orderId).filter(Boolean))];
    return ids.map(id=>{const o=(orders||[]).find(x=>x.id===id);return{id,label:o?(o.nm||o.cust?id+" · "+(o.nm||o.cust):id):id}}).sort((a,b)=>a.label.localeCompare(b.label));
  },[docs,orders]);

  // ── Filter + Sort ──
  const toggleSort=(key)=>{
    if(sortKey===key){setSortDir(d=>d==="asc"?"desc":"asc")}
    else{setSortKey(key);setSortDir("desc")}
  };

  const filtered=useMemo(()=>{
    let r=docs;
    if(filter!=="all")r=r.filter(d=>d.type===filter);
    if(custFilter!=="all")r=r.filter(d=>d.customer===custFilter);
    if(ordFilter!=="all")r=r.filter(d=>d.orderId===ordFilter);
    if(search){const q=search.toLowerCase();r=r.filter(d=>(d.docNum||"").toLowerCase().includes(q)||(d.orderName||"").toLowerCase().includes(q)||(d.customer||"").toLowerCase().includes(q)||(d.orderId||"").toLowerCase().includes(q))}
    // Sort
    r=[...r].sort((a,b)=>{
      let va,vb;
      if(sortKey==="doc"){va=(a.docNum||"").toLowerCase();vb=(b.docNum||"").toLowerCase();return sortDir==="asc"?va.localeCompare(vb):vb.localeCompare(va)}
      if(sortKey==="type"){va=a.type||"";vb=b.type||"";return sortDir==="asc"?va.localeCompare(vb):vb.localeCompare(va)}
      if(sortKey==="order"){va=(a.orderName||a.orderId||"").toLowerCase();vb=(b.orderName||b.orderId||"").toLowerCase();return sortDir==="asc"?va.localeCompare(vb):vb.localeCompare(va)}
      if(sortKey==="customer"){va=(a.customer||"").toLowerCase();vb=(b.customer||"").toLowerCase();return sortDir==="asc"?va.localeCompare(vb):vb.localeCompare(va)}
      if(sortKey==="edited"){va=new Date(a.updatedAt||0).getTime();vb=new Date(b.updatedAt||0).getTime();return sortDir==="asc"?va-vb:vb-va}
      // default: date (created)
      va=new Date(a.date||a.updatedAt||0).getTime();vb=new Date(b.date||b.updatedAt||0).getTime();return sortDir==="asc"?va-vb:vb-va;
    });
    return r;
  },[docs,filter,custFilter,ordFilter,search,sortKey,sortDir]);

  // ── Stats ──
  const stats=Object.entries(types).map(([k,v])=>({key:k,...v,count:docs.filter(d=>d.type===k).length}));
  const activeFilters=(filter!=="all"?1:0)+(custFilter!=="all"?1:0)+(ordFilter!=="all"?1:0)+(search?1:0);

  // ── Sortable column header ──
  const SortTh=({label,sKey,cls,style:sx})=>(
    <th className={cls||""} onClick={()=>toggleSort(sKey)} style={{padding:"10px 14px",textAlign:"left",fontWeight:700,fontSize:10,color:T.tm,textTransform:"uppercase",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap",...(sx||{})}}>
      <span style={{display:"inline-flex",alignItems:"center",gap:3}}>
        {label}
        {sortKey===sKey&&<span style={{fontSize:8,opacity:.7}}>{sortDir==="asc"?"▲":"▼"}</span>}
      </span>
    </th>
  );

  // ── Export helper ──
  const doExport=(items)=>{
    try{exportCSV(items,"smarttraq-trade-docs.csv",[
      {key:"docNum",label:"Doc #"},
      {key:"type",label:"Type"},
      {key:"orderId",label:"Order ID"},
      {key:"orderName",label:"Order Name"},
      {key:"customer",label:"Customer"},
      {key:"currency",label:"Currency"},
      {key:"date",label:"Created"},
      {key:"updatedAt",label:"Updated"},
    ])}catch(e){}
  };

  // ── Bulk delete ──
  const doBulkDel=(ids)=>{
    if(!confirm("Delete "+ids.length+" document"+(ids.length>1?"s":"")+"? This cannot be undone."))return;
    ids.forEach(id=>{if(onDelete)onDelete(id)});
    sSel(new Set());
    if(nt)nt(ids.length+" document"+(ids.length>1?"s":"")+" deleted");
  };

  // ── Empty ──
  if(docs.length===0)return <EmptyPg ic="fstk" title="No Trade Documents" sub="Open any order → Trade Docs tab → click Prepare to generate a Commercial Invoice or Packing List." />;

  return(<div>
    {/* ── Header ── */}
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
      <div>
        <h1 style={{fontSize:22,fontWeight:800,fontFamily:HF,margin:0,letterSpacing:"-.02em"}}>{t("tdoc.title")}</h1>
        <p style={{fontSize:12,color:T.tm,margin:"4px 0 0"}}>{docs.length} document{docs.length!==1?"s":""} across {new Set(docs.map(d=>d.orderId)).size} orders</p>
      </div>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        <button onClick={()=>doExport(filtered)} style={{...sB2G(),padding:"7px 14px",fontSize:10}}><Ic n="dl" s={11}/> Export{filtered.length<docs.length?" ("+filtered.length+")":""}</button>
      </div>
    </div>

    {/* ── Stats cards ── */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:16}} className="dash-stats">
      {stats.map(s=>(
        <div key={s.key} onClick={()=>setFilter(filter===s.key?"all":s.key)} className="cr" style={{padding:"14px 16px",borderRadius:14,border:"1px solid "+(filter===s.key?s.c+"30":isDark()?"rgba(255,255,255,.06)":T.bd),background:filter===s.key?(isDark()?s.c+"10":s.c+"06"):(isDark()?"rgba(255,255,255,.02)":T.sf),cursor:"pointer",transition:"all .15s"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
            <div style={{width:28,height:28,borderRadius:8,background:s.c+"15",display:"flex",alignItems:"center",justifyContent:"center",color:s.c}}><Ic n={s.ic} s={14}/></div>
            <span style={{fontSize:22,fontWeight:800,color:s.c,fontFamily:HF}}>{s.count}</span>
          </div>
          <div style={{fontSize:10,fontWeight:600,color:T.ts}}>{s.n}</div>
        </div>
      ))}
      {/* Total card */}
      <div onClick={()=>{setFilter("all");setCustFilter("all");setOrdFilter("all");setSearch("")}} className="cr" style={{padding:"14px 16px",borderRadius:14,border:"1px solid "+(activeFilters===0?T.ac+"30":isDark()?"rgba(255,255,255,.06)":T.bd),background:activeFilters===0?(isDark()?T.ac+"10":T.ac+"06"):(isDark()?"rgba(255,255,255,.02)":T.sf),cursor:"pointer",transition:"all .15s"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
          <div style={{width:28,height:28,borderRadius:8,background:T.ac+"15",display:"flex",alignItems:"center",justifyContent:"center",color:T.ac}}><Ic n="fstk" s={14}/></div>
          <span style={{fontSize:22,fontWeight:800,color:T.ac,fontFamily:HF}}>{docs.length}</span>
        </div>
        <div style={{fontSize:10,fontWeight:600,color:T.ts}}>All Documents</div>
      </div>
    </div>

    {/* ── Toolbar: Search + Filters ── */}
    <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
      <div style={{position:"relative",flex:"1 1 200px",maxWidth:280}}>
        <Ic n="srch" s={13}/>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search doc #, order, customer..." style={{...sIG(),paddingLeft:12,maxWidth:"100%"}}/>
      </div>
      {/* Type pill toggle */}
      <div style={{display:"flex",gap:3,background:T.sl,borderRadius:10,padding:2,border:"1px solid "+T.bd+"60"}}>
        {[["all","All"],["ci","CI"],["pl","PL"]].map(([k,l])=><button key={k} onClick={()=>setFilter(k)} style={{padding:"5px 12px",borderRadius:8,border:"none",background:filter===k?T.ac:"transparent",color:filter===k?"#FFF":T.tm,cursor:"pointer",fontFamily:"'Inter',system-ui,-apple-system,sans-serif",fontSize:10,fontWeight:600}}>{l}</button>)}
      </div>
      {/* Customer filter */}
      {custList.length>1&&<select value={custFilter} onChange={e=>setCustFilter(e.target.value)} style={{...sSG(),maxWidth:180,padding:"6px 10px",fontSize:11}}>
        <option value="all">All Customers</option>
        {custList.map(c=><option key={c} value={c}>{c}</option>)}
      </select>}
      {/* Order filter */}
      {ordList.length>1&&<select value={ordFilter} onChange={e=>setOrdFilter(e.target.value)} style={{...sSG(),maxWidth:200,padding:"6px 10px",fontSize:11}}>
        <option value="all">All Orders</option>
        {ordList.map(o=><option key={o.id} value={o.id}>{o.label}</option>)}
      </select>}
      {/* Active filter count badge + clear */}
      {activeFilters>0&&<button onClick={()=>{setFilter("all");setCustFilter("all");setOrdFilter("all");setSearch("")}} style={{padding:"5px 12px",borderRadius:8,border:"1px solid "+T.ac+"30",background:T.ac+"08",color:T.ac,cursor:"pointer",fontFamily:"'Inter',system-ui,-apple-system,sans-serif",fontSize:10,fontWeight:700,display:"flex",alignItems:"center",gap:4}}>
        <Ic n="x" s={10}/> Clear {activeFilters} filter{activeFilters>1?"s":""}
      </button>}
    </div>

    {/* ── Empty filtered state ── */}
    {filtered.length===0?<div style={{padding:48,textAlign:"center",border:"1px dashed "+T.bd,borderRadius:14,background:isDark()?"rgba(255,255,255,.01)":"rgba(0,0,0,.01)"}}>
      <div style={{width:48,height:48,borderRadius:18,background:"rgba(30,64,175,.08)",display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:12}}><Ic n="fstk" s={24}/></div>
      <div style={{fontWeight:700,fontSize:16,color:T.tx,marginBottom:6,fontFamily:HF}}>No Documents Match</div>
      <div style={{fontSize:12,color:T.tm,maxWidth:400,margin:"0 auto",lineHeight:1.6}}>Try adjusting your filters or search query.</div>
    </div>

    /* ── Table ── */
    :<div className="table-scroll" style={{background:T.sf+"80",borderRadius:12,border:"1px solid "+T.bd,overflow:"hidden"}}>
      {/* Bulk actions bar */}
      {sel.size>0&&<div style={{padding:"8px 14px",background:T.ac+"10",borderBottom:"1px solid "+T.ac+"20",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",animation:"su .15s ease"}}>
        <span style={{fontSize:11,fontWeight:700,color:T.ac}}>{sel.size} selected</span>
        <button onClick={()=>doExport(filtered.filter(d=>sel.has(d.id)))} style={{padding:"4px 10px",borderRadius:6,border:"1px solid "+T.gn,background:T.gn+"08",color:T.gn,cursor:"pointer",fontFamily:"'Inter',system-ui,-apple-system,sans-serif",fontSize:10,fontWeight:600}}>Export</button>
        {onDelete&&<button onClick={()=>doBulkDel([...sel])} style={{padding:"4px 10px",borderRadius:6,border:"1px solid "+T.rd,background:T.rd+"08",color:T.rd,cursor:"pointer",fontFamily:"'Inter',system-ui,-apple-system,sans-serif",fontSize:10,fontWeight:600}}>Delete ({sel.size})</button>}
        <button onClick={()=>sSel(new Set())} style={{padding:"4px 10px",borderRadius:6,border:"1px solid "+T.bd,background:"transparent",color:T.tm,cursor:"pointer",fontFamily:"'Inter',system-ui,-apple-system,sans-serif",fontSize:10,fontWeight:600,marginLeft:"auto"}}>Clear</button>
      </div>}

      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
        <thead>
          <tr style={{background:T.sl}}>
            <th style={{padding:"10px 8px",width:32}}><input type="checkbox" checked={sel.size===filtered.length&&filtered.length>0} onChange={()=>sSel(sel.size===filtered.length?new Set():new Set(filtered.map(d=>d.id)))} style={{cursor:"pointer"}}/></th>
            <SortTh label="Document" sKey="doc"/>
            <SortTh label="Type" sKey="type"/>
            <SortTh label="Order" sKey="order"/>
            <SortTh label="Customer" sKey="customer" cls="mob-td-hide"/>
            <th className="mob-td-hide" style={{padding:"10px 14px",textAlign:"left",fontWeight:700,fontSize:10,color:T.tm,textTransform:"uppercase"}}>Currency</th>
            <SortTh label="Created" sKey="date" cls="mob-td-hide"/>
            <SortTh label="Edited" sKey="edited" cls="mob-td-hide"/>
            <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,fontSize:10,color:T.tm,textTransform:"uppercase"}}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(d=>{
            const tp=types[d.type]||{n:d.type,s:"?",c:T.ac,ic:"file"};
            const fmtD=(v)=>{if(!v)return"—";try{const dt=v.includes("T")?new Date(v):new Date(v+"T00:00:00");return dt.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}catch(e){return"—"}};
            return(
            <tr key={d.id} className="cr" style={{borderBottom:"1px solid "+T.bd,cursor:"pointer",transition:"background .12s ease",background:sel.has(d.id)?T.ac+"06":"transparent"}} onClick={()=>{if(onView)onView(d)}}>
              <td style={{padding:"12px 8px"}} onClick={e=>e.stopPropagation()}><input type="checkbox" checked={sel.has(d.id)} onChange={()=>togSel(d.id)} style={{cursor:"pointer"}}/></td>
              {/* Document */}
              <td style={{padding:"12px 14px"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                  <div style={{width:32,height:32,borderRadius:9,background:tp.c+"12",display:"flex",alignItems:"center",justifyContent:"center",color:tp.c,flexShrink:0}}><Ic n={tp.ic} s={14}/></div>
                  <div style={{minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.docNum}</div>
                  </div>
                </div>
              </td>
              {/* Type badge */}
              <td style={{padding:"12px 14px"}}><span style={{fontSize:9,fontWeight:700,padding:"3px 10px",borderRadius:6,background:tp.c+"12",color:tp.c,display:"inline-block"}}>{tp.s}</span></td>
              {/* Order ref */}
              <td style={{padding:"12px 14px"}}><span style={{fontSize:11,fontWeight:600,color:T.cy,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"block"}}>{d.orderName||d.orderId||"—"}</span></td>
              {/* Customer */}
              <td className="mob-td-hide" style={{padding:"12px 14px",fontSize:11,color:T.ts,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.customer||"—"}</td>
              {/* Currency */}
              <td className="mob-td-hide" style={{padding:"12px 14px",fontSize:11,fontWeight:600,color:T.tx}}>{d.currency||"USD"}</td>
              {/* Created */}
              <td className="mob-td-hide" style={{padding:"12px 14px",fontSize:11,color:T.ts}}>{fmtD(d.date)}</td>
              {/* Edited */}
              <td className="mob-td-hide" style={{padding:"12px 14px",fontSize:11,color:T.tm}}>{d.updatedAt?fmtD(d.updatedAt):"—"}</td>
              {/* Actions */}
              <td style={{padding:"12px 14px"}}>
                <div style={{display:"flex",gap:4,justifyContent:"flex-end"}} onClick={e=>e.stopPropagation()}>
                  <DotsMenu items={[
                    {ic:"eye",l:"View",act:()=>{if(onView)onView(d)}},
                    onEdit&&{ic:"pen",l:"Edit in Order",act:()=>onEdit(d)},
                    onPrint&&{ic:"print",l:"Print / PDF",act:()=>onPrint(d)},
                    {sep:true},
                    onDelete&&{ic:"del",l:"Delete",danger:true,act:()=>{if(confirm("Delete "+d.docNum+"?")){onDelete(d.id);if(nt)nt(d.docNum+" deleted")}}}
                  ].filter(Boolean)}/>
                </div>
              </td>
            </tr>
          )})}
        </tbody>
      </table>

      {/* Results footer */}
      <div style={{padding:"10px 18px",borderTop:"1px solid "+T.bd+"40",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontSize:10,color:T.tm}}>Showing {filtered.length} of {docs.length} document{docs.length!==1?"s":""}</span>
        {activeFilters>0&&<span style={{fontSize:10,color:T.ac,fontWeight:600}}>{activeFilters} filter{activeFilters>1?"s":""} active</span>}
      </div>
    </div>}
  </div>);
}

export default TradeDocsPg;
